import { useEffect, useState } from "react";
import styled from "styled-components";

import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Button from "src/components/core/Button";

import LabelSection from "src/components/core/LabelSection";
import MetaTagComponent from "src/modules/exclusive-deal/MataTagComponent";

import EditIcon from "@mui/icons-material/Edit";

import { useDialog } from "src/context/DialogContext";
import { useSnackbar } from "notistack";

import { hexToRgba } from "src/common/color";
import { Black, DarkGrey, White, White01 } from "src/styles/color";
import font from "src/styles/font";

import { getAllSections, editMetaTag } from "src/api/landingPage";

const S = {};

S.BoxWrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 32px;
  border-radius: 32px;
  background-color: ${White};
  box-shadow: 0px 4px 20px ${hexToRgba(Black, 0.1)};
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
`;

S.PreviewImageUploadBox = styled.div`
  position: relative;
  padding-top: 100%;
  background-color: ${White01};
  border-radius: 16px;
  /* grid-column: span 7; */
`;

S.MetaTagPreviewImage = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 15px;
  object-fit: cover;
`;

S.MetaTagLabel = styled.div`
  font-size: ${font.size.xs};
  font-weight: 400;
  color: ${DarkGrey};
`;

S.MetaTagDetail = styled.div`
  font-size: ${font.size.md};
  font-weight: 400;
  color: ${Black};
`;

const MetaTag = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog, closeDialog } = useDialog();

  const [data, setData] = useState({
    meta_tag: {
      title: "",
      desc: "",
      img: null,
      keywords: "",
    },
  });

  // Meta tag
  const [editingMetaTag, setEditingMetaTag] = useState({
    title: "",
    desc: "",
    img: null,
    keywords: "",
  });

  const [isMetaTagEdit, setIsMetaTagEdit] = useState(false);

  /**
   * @dev check meta tag require field is valid
   */
  const isMetaTagValid = Object.keys(editingMetaTag).every((required) => {
    let objVal = editingMetaTag[required];
    return objVal !== null && objVal !== "";
  });

  const handleEditMetaTag = async () => {
    try {
      const payload = {
        ...editingMetaTag,
      };

      const data = new FormData();
      for (const field in payload) {
        data.append(field, payload[field]);
      }

      await editMetaTag(data);

      setEditingMetaTag({
        title: "",
        desc: "",
        img: null,
        keywords: "",
      });
      setIsMetaTagEdit(false);

      enqueueSnackbar(`แก้ไขสำเร็จ`, {
        variant: "success",
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMetaTag = async () => {
    try {
      const result = await getAllSections();
      setData(result);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchMetaTag();
  }, []);

  return (
    <S.BoxWrapper>
      <LabelSection
        title="Metatag"
        headerStyle={{ marginBottom: "24px" }}
        {...(!isMetaTagEdit && {
          actionMenu: () => (
            <Button
              startIcon={<EditIcon />}
              variant="secondary"
              size="large"
              sx={{
                minWidth: 200,
              }}
              onClick={() => {
                setIsMetaTagEdit(true);
                setEditingMetaTag(data.meta_tag);
              }}
            >
              แก้ไข
            </Button>
          ),
        })}
      >
        {!isMetaTagEdit ? (
          <Grid container>
            <Grid
              item
              xs={9}
              md={9.5}
              sx={{
                pr: 3,
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <Stack columnGap={1}>
                <S.MetaTagLabel>หัวเรื่อง</S.MetaTagLabel>
                <S.MetaTagDetail>
                  {data?.meta_tag?.title || "-"}
                </S.MetaTagDetail>
              </Stack>

              <Stack columnGap={1}>
                <S.MetaTagLabel>รายละเอียด</S.MetaTagLabel>
                <S.MetaTagDetail>{data?.meta_tag?.desc || "-"}</S.MetaTagDetail>
              </Stack>

              <Stack columnGap={1}>
                <S.MetaTagLabel>คีย์เวิร์ด</S.MetaTagLabel>
                <S.MetaTagDetail>
                  {data?.meta_tag?.keywords || "-"}
                </S.MetaTagDetail>
              </Stack>
            </Grid>

            <Grid item xs={3} md={2.5}>
              <S.PreviewImageUploadBox>
                <S.MetaTagPreviewImage
                  src={
                    !!data?.meta_tag?.img
                      ? typeof data?.meta_tag?.img === "string"
                        ? data?.meta_tag?.img
                        : URL.createObjectURL(data?.meta_tag?.img)
                      : ""
                  }
                  alt=""
                />
              </S.PreviewImageUploadBox>
            </Grid>
          </Grid>
        ) : (
          <MetaTagComponent
            keyImg="metaTagIconFieldHandle"
            metatagData={editingMetaTag}
            setMetatagData={setEditingMetaTag}
            isEdit={isMetaTagEdit}
          />
        )}
      </LabelSection>

      {isMetaTagEdit && (
        <Stack
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-end"
          gap={3}
        >
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              openDialog({
                type: "confirm",
                title: `คุณต้องการยกเลิกการแก้ไขใช่หรือไม่?`,
                onConfirm: () => {
                  setEditingMetaTag({
                    title: "",
                    desc: "",
                    img: null,
                    keywords: "",
                  });
                  setIsMetaTagEdit(false);
                  closeDialog();
                },
                maxWidth: 342,
              });
            }}
            sx={{
              minWidth: 160,
            }}
          >
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              openDialog({
                type: "confirm",
                title: `คุณต้องการบันทึกการแก้ไขใช่หรือไม่`,
                onConfirm: async () => {
                  await handleEditMetaTag();
                  await fetchMetaTag();
                },
                maxWidth: 342,
              });
            }}
            sx={{
              minWidth: 160,
            }}
            // autoFocus // enable auto focus to increase accessibility
            disabled={!isMetaTagValid}
          >
            บันทึก
          </Button>
        </Stack>
      )}
    </S.BoxWrapper>
  );
};

export default MetaTag;
